/**
 * The tea towel draped over a range's oven handle — its drape, and therefore
 * its box and where the HANDLE sits inside that box.
 *
 * Three consumers, one source:
 *   - the renderer draws the towel wrapping a bar of this radius;
 *   - `STAGING_PROP_DIMS['staging-tea-towel']` is derived from it, so the box
 *     the containment test checks is the box the renderer fills;
 *   - the kitchen planner seats the box so its bar line sits on the range's
 *     handle (`rangeFrontLayout`), which needs `barFromCentre`.
 *
 * Drape (inches): folded in thirds to 9 in wide, over the handle, a 6.5 in
 * back drop between the handle and the oven door and a 10.5 in front drop —
 * the reviewed prototype. The wrap radius is the handle's radius plus the
 * towel's own thickness, so the cloth lies on the bar rather than around air.
 *
 * Local frame of the drape: origin at the handle's axis, +x along the
 * handle, +y up, +z out of the oven's front.
 */

const { RANGE_HANDLE_RADIUS } = require('../kitchen-assembly/rangeFront');
const { objectShowsGlb } = require('../fixture-types/productRepresentation');

const IN = 0.0254;
const TEA_TOWEL_WIDTH_IN = 9;
const TEA_TOWEL_BACK_DROP_IN = 6.5;
const TEA_TOWEL_FRONT_DROP_IN = 10.5;
const TEA_TOWEL_THICKNESS_IN = 0.2;
const TEA_TOWEL_WRAP_R_IN = RANGE_HANDLE_RADIUS / IN + TEA_TOWEL_THICKNESS_IN;
/** How far the back drop leans in toward the door, per inch of drop. */
const TEA_TOWEL_BACK_LEAN = 0.02;
/** How far the front drop swings out, per inch of drop, plus its ripple. */
const TEA_TOWEL_FRONT_SWING = 0.05;
const TEA_TOWEL_RIPPLE_IN = 0.14;

/**
 * Where a point `s` inches along the cloth (from the bottom of the back drop,
 * over the handle, to the bottom of the front drop) lies in the drape frame.
 * `x` is across the towel, for the ripple.
 */
function teaTowelDrapePoint(s, x = 0) {
  const R = TEA_TOWEL_WRAP_R_IN;
  const back = TEA_TOWEL_BACK_DROP_IN;
  const arc = Math.PI * R;
  if (s < back) return { y: -back + s, z: -R - TEA_TOWEL_BACK_LEAN * s };
  if (s < back + arc) {
    const t = (s - back) / R;
    return { y: Math.sin(t) * R, z: -Math.cos(t) * R };
  }
  const d = s - back - arc;
  return {
    y: -d,
    z:
      R +
      TEA_TOWEL_FRONT_SWING * d +
      Math.sin(d * 0.6) * 0.08 +
      Math.sin(x * 0.9) * 0.06,
  };
}

/** Total cloth length along the drape, inches. */
const teaTowelClothLength = () =>
  TEA_TOWEL_BACK_DROP_IN +
  Math.PI * TEA_TOWEL_WRAP_R_IN +
  TEA_TOWEL_FRONT_DROP_IN;

/**
 * The towel's box, in inches, and the handle axis relative to the box centre.
 * `barFromCentre.y` is positive: the handle is near the TOP of the box.
 */
function teaTowelBox() {
  const R = TEA_TOWEL_WRAP_R_IN;
  const top = R;
  const bottom = -TEA_TOWEL_FRONT_DROP_IN;
  const backZ = -R - TEA_TOWEL_BACK_LEAN * TEA_TOWEL_BACK_DROP_IN;
  const frontZ =
    R + TEA_TOWEL_FRONT_SWING * TEA_TOWEL_FRONT_DROP_IN + TEA_TOWEL_RIPPLE_IN;
  const centreY = (top + bottom) / 2;
  const centreZ = (backZ + frontZ) / 2;
  return {
    x: TEA_TOWEL_WIDTH_IN,
    y: top - bottom,
    z: frontZ - backZ,
    barFromCentre: { y: -centreY, z: -centreZ },
  };
}

/**
 * Is this a hung tea towel that has nothing to hang on? The planner hangs the
 * towel only on a range we DRAW (its handle is known); the room can change
 * after staging — the range swapped for a catalog model, whose handle could be
 * anywhere, or deleted. Then the towel is not drawn, in any view, rather than
 * hanging in front of a product or in mid-air. Plain type strings, not
 * `objectTypeOf`: `ownership` reaches this module through `propDims`.
 *
 * @param {Object} obj  any room object
 * @param {Object[]} roomObjects  the objects its parent is among
 */
function teaTowelHasNoHandle(obj, roomObjects) {
  if (obj?.type !== 'staging-tea-towel') return false;
  const parentId = obj.parents?.[0]?.id;
  const range = (roomObjects || []).find((o) => o?.id === parentId);
  // The planner's own reading of "a range" (`objectTypeOf`: lower-cased, the
  // category when there is no type), or a towel it hung is hidden here.
  // Required here, not at the top: propDims requires this module while it
  // loads, and ownership requires propDims.
  const { objectTypeOf } = require('./ownership');
  return !range || objectTypeOf(range) !== 'range' || objectShowsGlb(range);
}

module.exports = {
  teaTowelHasNoHandle,
  TEA_TOWEL_WIDTH_IN,
  TEA_TOWEL_BACK_DROP_IN,
  TEA_TOWEL_FRONT_DROP_IN,
  TEA_TOWEL_WRAP_R_IN,
  teaTowelDrapePoint,
  teaTowelClothLength,
  teaTowelBox,
};
